// Vendor imports
const React = require('react')
const PropTypes = require('prop-types')
const classNames = require('classnames')
// Shared imports
const BemEntity = require('shared/bem')
const {
  ARTICLE_SUBTYPES: articleSubtypes,
  ARTICLE_SUBTYPES_KEYS: articleSubtypeKeys
} = require('shared/model-types')
// Common client-v2 imports
const { ArticleAddCircleButton } = require('client-v2/common/components/buttons')
const LazyImage = require('client-v2/common/components/lazy-image')
const { registerEventHandlerShim } = require('client-v2/common/components/utils')
const { articleDomainSearchHref } = require('client-v2/common/models/article/actions')
// Container imports
const { TabNameEnum } = require('../state/slide-record')
const ArticleProfileCollections = require('./collections')
const ArticleProfileComments = require('./comments-smart')
// Convenience; in lieu of JSX transpiling...
const { createElement: tag } = React

const bem = BemEntity('ArticleProfileDetailCard')

const callbackKeys = [
  'onAddClick',
  'onContentClick',
  'onDomainClick',
  'onMoreActionsClick',
  'onMoreCollectionsClick',
  'onShareClick'
]

class ArticleProfileDetailCard extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      isEmbedVisible: props.isEmbedVisible
    }

    // Ensure function references do not change between render calls
    this.onTabSelect = {}
    Object.values(TabNameEnum).forEach(enumValue => {
      this.onTabSelect[enumValue] = () => {
        this.props.onTabSelect({
          article: this.props.article,
          selectedTab: enumValue
        })
      }
    })
    this.toggleEmbed = this.toggleEmbed.bind(this)
    callbackKeys.forEach(key => registerEventHandlerShim(this, key))
  }

  toggleEmbed(event) {
    event.stopPropagation()
    this.setState({ isEmbedVisible: !this.state.isEmbedVisible })
  }

  // Render methods

  renderActions() {
    return (
      tag('div', {
        className: bem.element('actions')
      },
        tag('button', {
          className: bem.element('action-button', 'more'),
          title: 'Show more actions for this article',
          onClick: this.onMoreActionsClick
        }),
        tag('button', {
          className: bem.element('action-button', 'share'),
          title: 'Share this article with a friend',
          onClick: this.onShareClick
        }),
        tag(ArticleAddCircleButton, {
          className: bem.element('action-button', 'add'),
          checked: this.props.article.hasMix,
          title: 'Add this article to your collections',
          onClick: this.onAddClick
        })
      )
    )
  }

  renderCollections() {
    const tab = this.props.tabs[TabNameEnum.COLLECTIONS]
    if (!tab.isSelected) {
      return null
    }
    return (
      tag(ArticleProfileCollections, {
        className: bem.element('collections'),
        // Flags
        isMoreAvailable: this.props.isMoreCollectionsAvailable,
        // Values
        items: this.props.collections,
        // Callbacks
        onMoreClick: this.onMoreCollectionsClick
      })
    )
  }

  renderComments() {
    const tab = this.props.tabs[TabNameEnum.COMMENTS]
    if (!tab.isSelected) {
      return null
    }
    return (
      tag(ArticleProfileComments, {
        className: bem.element('comments'),
        // Flags
        isLoading: tab.isLoading,
        // Values
        article: this.props.article,
        items: this.props.comments,
        user: this.props.user
      })
    )
  }

  renderContent() {
    return (
      tag('div', {
        className: bem.element('content')
      },
        this.renderImageLeading(),
        this.renderDomain(),
        this.renderTitle(),
        this.renderDescription(),
        this.renderImageTrailing()
      )
    )
  }

  renderDescription() {
    const { article: { description, url } } = this.props
    if (!description) {
      return null
    }
    return (
      tag('a', {
        className: bem.element('description'),
        href: url,
        target: '_blank',
        title: description,
        onClick: this.onContentClick,
        rel: 'nofollow'
      },
        description
      )
    )
  }

  renderDomain() {
    const { article } = this.props
    return (
      tag('a', {
        className: bem.element('domain'),
        href: articleDomainSearchHref(article),
        title: article.hostname,
        onClick: this.onDomainClick
      },
        article.hostname
      )
    )
  }

  renderEmbeddedPlayer() {
    if (!this.state.isEmbedVisible) {
      return tag('div', {
        className: bem.element('embed-curtain'),
        onClick: this.toggleEmbed
      })
    }
    const { article } = this.props
    return (
      tag('div', {
        className: bem.element('embedded-player'),
        dangerouslySetInnerHTML: { __html: article.video }
      })
    )
  }

  renderImage(modifiers, ...children) {
    const {
      article: { image, md5image, url }
    } = this.props
    return (
      tag('div', {
        className: bem.element('image-container', modifiers),
        onClick: children.length ? null : this.onContentClick
      },
        tag(LazyImage, {
          className: bem.element('image'),
          href: url,
          target: '_blank',
          sources: [md5image, image],
          rel: 'nofollow'
        }),
        ...children
      )
    )
  }

  renderImageLeading() {
    switch (this.props.article.subtype) {
      case articleSubtypes.AUDIO:
      case articleSubtypes.VIDEO:
        return this.renderImage('leading',
          this.renderEmbeddedPlayer()
        )
      case articleSubtypes.IMAGE:
        return this.renderImage('leading')
      default:
        return null
    }
  }

  renderImageTrailing() {
    switch (this.props.article.subtype) {
      case articleSubtypes.TEXT_WITH_IMAGE:
        return this.renderImage('trailing')
      default:
        return null
    }
  }

  renderNav() {
    return (
      tag('nav', {
        className: bem.element('nav')
      },
        this.renderActions(),
        this.renderTabs()
      )
    )
  }

  renderTab(enumValue) {
    const tab = this.props.tabs[enumValue]
    return (
      tag('li', {
        className: bem.element('tab', {
          [enumValue]: true,
          loading: tab.isLoading,
          selected: tab.isSelected
        }),
        title: tab.label,
        onClick: this.onTabSelect[enumValue]
      },
        tab.label
      )
    )
  }

  renderTabs() {
    return (
      tag('ul', {
        className: bem.element('tabs')
      },
        this.renderTab(TabNameEnum.COLLECTIONS),
        this.renderTab(TabNameEnum.COMMENTS)
      )
    )
  }

  renderTitle() {
    const { article: { title, url } } = this.props
    return (
      tag('a', {
        className: bem.element('title'),
        href: url,
        target: '_blank',
        title,
        onClick: this.onContentClick,
        rel: 'nofollow'
      },
        title
      )
    )
  }

  render() {
    // Prefer the subtype enum key in the stylesheet instead of the value
    const subtype = articleSubtypeKeys[this.props.article.subtype]
    return (
      tag('article', {
        className: classNames(
          this.props.className,
          bem.block({ subtype })
        )
      },
        this.renderContent(),
        this.renderNav(),
        this.renderCollections(),
        this.renderComments()
      )
    )
  }
}

const tabPropTypes = {
  isLoading: PropTypes.bool,
  isSelected: PropTypes.bool,
  label: PropTypes.string.isRequired
}

ArticleProfileDetailCard.propTypes = {
  // Flags
  isEmbedVisible: PropTypes.bool,
  isMoreCollectionsAvailable: PropTypes.bool,
  // Values
  article: PropTypes.object.isRequired,
  className: PropTypes.string,
  collections: PropTypes.arrayOf(PropTypes.object).isRequired,
  comments: PropTypes.arrayOf(PropTypes.object).isRequired,
  tabs: PropTypes.shape({
    [TabNameEnum.COLLECTIONS]: PropTypes.shape(tabPropTypes),
    [TabNameEnum.COMMENTS]: PropTypes.shape(tabPropTypes)
  }).isRequired,
  user: PropTypes.object.isRequired,
  // Callbacks
  onTabSelect: PropTypes.func.isRequired
}

// Callback propTypes
callbackKeys.forEach(key => {
  ArticleProfileDetailCard.propTypes[key] = PropTypes.func.isRequired
})

ArticleProfileDetailCard.defaultProps = {
  isEmbedVisible: true // Set to `false` to enable the embed curtain
}

module.exports = ArticleProfileDetailCard
